// Thin wrappers around the admin/userBuilder.ts httpActions.
// All calls reuse adminAPI's session token via the shared
// `request<T>` helper in api.ts.

import { adminAPI, adminSession } from './api';
import { activeConfig } from './environments';
import { ensureH264 } from './transcodeVideo';
import {
  extractPosterFrame,
  putToR2,
  queueFfmpeg,
  readVideoDurationSeconds,
} from './videoHelpers';

// Resolved at runtime from the active environment — the profile builder
// must follow the env toggle like the rest of the console, or it writes
// to the wrong deployment.
const API_BASE = activeConfig().apiBase;

// ─── Token-aware fetch (re-using admin session) ────────────────────────────

class BuilderError extends Error {
  constructor(public code: string, message: string) {
    super(message);
  }
}

async function rawRequest<T>(
  path: string,
  init: { method?: string; body?: unknown; headers?: Record<string, string> },
): Promise<T> {
  const token = adminSession.getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers ?? {}),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(API_BASE + path, {
    method: init.method ?? 'GET',
    headers,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = data?.error ?? {};
    throw new BuilderError(err.code ?? 'UNKNOWN', err.message ?? 'Request failed');
  }
  return data as T;
}

// ─── DTOs that wrap the wire shape ────────────────────────────────────────

export type ThemeSong = {
  trackId: string;
  trackName: string;
  artistName: string;
  albumArtUrl?: string;
  durationMs: number;
  spotifyUrl: string;
};

export type Location = {
  latitude: number;
  longitude: number;
  city: string;
  country: string;
  countryCode?: string;
  source: 'auto' | 'manual';
};

export type ProfilePatch = {
  dob?: string;            // "YYYY-MM-DD" — required for publish, optional for save
  bio?: string;
  gender?: string;
  lookingFor?: string[];
  education?: string;
  educationLevel?: string;
  job?: string;
  hometown?: string;
  religion?: string;
  politics?: string;
  languages?: string[];
  vices?: string[];
  datingIntentions?: string[];
  promptAnswers?: Array<{ promptId: string; answer: string }>;
  themeSong?: ThemeSong | null;
  location?: Location | null;
};

// ─── Operations ───────────────────────────────────────────────────────────

export const builderAPI = {
  /**
   * Create a fresh user (auth shell + force-verify email). Returns
   * the new userId — every subsequent call is scoped to it.
   */
  createUser: (body: {
    email: string;
    password: string;
    display_name?: string;
  }) => rawRequest<{ user_id: string }>('/admin/users/full', {
    method: 'POST',
    body,
  }),

  /**
   * Upload a single photo. Presign → PUT to R2 → finalize. Photos
   * land in state='published' immediately (admin trust bypass).
   */
  uploadPhoto: async (
    userId: string,
    file: File,
    onProgress?: (pct: number) => void,
  ) => {
    const presign = await rawRequest<{ upload_url: string; key: string }>(
      `/admin/users/${userId}/photos/presign`,
      { method: 'POST', body: { content_type: file.type } },
    );
    await putToR2(presign.upload_url, file, file.type, onProgress ?? (() => {}));
    return rawRequest<{ id: string; position: number }>(
      `/admin/users/${userId}/photos/finalize`,
      {
        method: 'POST',
        body: {
          key: presign.key,
          content_type: file.type,
          size_bytes: file.size,
        },
      },
    );
  },

  /**
   * Reorder photos. mediaIds are the new ordered sequence
   * — position 1 (first entry) is the lead/cover photo.
   */
  reorderPhotos: (userId: string, mediaIds: string[]) =>
    rawRequest<{ ok: true }>(
      `/admin/users/${userId}/photos/reorder`,
      { method: 'POST', body: { media_ids: mediaIds } },
    ),

  /**
   * Upload a single video. Reads duration, transcodes to H.264 if
   * needed, extracts a poster frame, presigns both, PUTs in
   * parallel, finalizes. Stages are reported via onStage so the
   * UI can render an accurate progress label.
   */
  uploadVideo: async (
    userId: string,
    file: File,
    onStage: (stage: 'probe' | 'transcode' | 'upload' | 'finalize', pct?: number) => void,
  ) => {
    onStage('probe');
    const durationSeconds = await readVideoDurationSeconds(file);

    onStage('transcode', 0);
    const mp4 = await queueFfmpeg(() =>
      ensureH264(file, (pct) => onStage('transcode', pct)),
    );

    let posterBlob: Blob | null = null;
    try {
      posterBlob = await extractPosterFrame(mp4);
    } catch (e) {
      console.warn('[admin] poster extraction failed', e);
    }

    const presign = await rawRequest<{
      videoId: string;
      uploadUrl: string;
      storageKey: string;
      posterUploadUrl: string;
      posterStorageKey: string;
    }>(`/admin/users/${userId}/videos/presign`, {
      method: 'POST',
      body: {
        size_bytes: mp4.size,
        duration_seconds: durationSeconds,
        mime_type: 'video/mp4',
      },
    });

    try {
      onStage('upload', 0);
      const uploads: Array<Promise<void>> = [
        putToR2(presign.uploadUrl, mp4, 'video/mp4', (pct) =>
          onStage('upload', pct),
        ),
      ];
      if (posterBlob) {
        uploads.push(
          putToR2(
            presign.posterUploadUrl,
            posterBlob,
            'image/jpeg',
            () => {},
          ),
        );
      }
      await Promise.all(uploads);
      onStage('finalize');
      return rawRequest<{ state: string }>(
        `/admin/users/${userId}/videos/finalize`,
        {
          method: 'POST',
          body: {
            video_id: presign.videoId,
            poster_uploaded: !!posterBlob,
          },
        },
      );
    } catch (uploadErr) {
      // Best-effort mark-error so the row doesn't linger in 'uploading'.
      await rawRequest(`/admin/users/${userId}/videos/error`, {
        method: 'POST',
        body: { video_id: presign.videoId },
      }).catch(() => null);
      throw uploadErr;
    }
  },

  /** PATCH the profile row. Auto-vivifies if missing. */
  patchProfile: (userId: string, patch: ProfilePatch) =>
    rawRequest<{ ok: true; profile_id: string }>(
      `/admin/users/${userId}/profile`,
      { method: 'PATCH', body: patch },
    ),

  /** Publish on behalf of the user. Requires ≥1 photo. */
  publish: (userId: string) =>
    rawRequest<{ slug: string; public_url: string }>(
      `/admin/users/${userId}/publish`,
      { method: 'POST' },
    ),

  /**
   * Ask DeepSeek for a bio + prompt answers. Caller passes the
   * picked prompts and gets matched answers back. Throws
   * BuilderError('DEEPSEEK_UNAVAILABLE') on transient failure;
   * caller falls back to faker-only fill.
   */
  generateContent: (body: {
    gender: string;
    looking_for: string[];
    dating_intentions?: string[];
    vices?: string[];
    job?: string;
    education?: string;
    prompts: Array<{ id: string; text: string }>;
  }) => rawRequest<{
    bio: string;
    prompt_answers: Array<{ prompt_id: string; answer: string }>;
  }>('/admin/users/generate-content', { method: 'POST', body }),
};

// Exported so callers can `instanceof` check it.
export { BuilderError };

// Re-export adminAPI for convenience in components that need both.
export { adminAPI };
